import {
  Component,
  signal,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { TranslationService } from '../../../core/services/i18n/translation.service';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { NotificationCenterService } from '../../../core/services/notification-center.service';
import { AttachmentFilingService } from '../../../core/services/attachment-filing.service';
import { filter, interval, Subject, takeUntil } from 'rxjs';

const TO_FILE_COUNT_EVERY_MS = 5 * 60_000;

interface NavItem {
  icon: string;
  route: string;
  translationKey: string;
  /** Opens a page behind staffGuard: hidden from a user with no role */
  staffOnly?: true;
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, LanguageSwitcherComponent],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy {
  isMobileMenuOpen = signal(false);
  showUserMenu = signal(false);

  private destroy$ = new Subject<void>();

  navItems: NavItem[] = [
    {
      icon: 'fas fa-clipboard-list',
      route: '/orders',
      translationKey: 'orders',
    },
    {
      icon: 'fas fa-file-alt',
      route: '/documents',
      translationKey: 'documents',
    },
    { icon: 'fas fa-funnel-dollar', route: '/leads', translationKey: 'leads', staffOnly: true },
    { icon: 'fas fa-users', route: '/customers', translationKey: 'customers' },
    { icon: 'fas fa-truck', route: '/suppliers', translationKey: 'suppliers', staffOnly: true },
    { icon: 'fas fa-boxes', route: '/products', translationKey: 'products' },
    {
      icon: 'fas fa-envelope-open-text',
      route: '/emails',
      translationKey: 'emailProcessing',
      staffOnly: true,
    },
    {
      icon: 'fas fa-bolt',
      route: '/realtime-activity',
      translationKey: 'realtimeActivity',
    },
    {
      icon: 'fas fa-chart-bar',
      route: '/analytics',
      translationKey: 'analytics',
      staffOnly: true,
    },
  ];

  /**
   * A user with no role does not see the staff pages. Home, first, opens the landing
   * page, and is left out when another item already opens it (order staff land on Orders).
   * An executive (the super admin too) lands on the CEO dashboard: the first item names it.
   */
  visibleNavItems = computed(() => {
    const staff = this.authService.canManageOrders();
    const items = this.navItems.filter((item) => staff || !item.staffOnly);
    if (this.authService.hasExecutiveAccess()) {
      return [
        { icon: 'fas fa-chart-line', route: '/executive-dashboard', translationKey: 'ceo' },
        ...items,
      ];
    }
    const home = this.authService.getLandingRoute();
    if (items.some((item) => item.route === home)) return items;
    return [{ icon: 'fas fa-home', route: home, translationKey: 'home' }, ...items];
  });

  /**
   * The phone tab bar (card 835, MOR-D35 A): Home, Orders, Customers, Emails, then "More" for
   * the full menu. Home is the landing page, also for sales, whose home is the order timeline.
   */
  tabItems = computed(() => {
    const items = this.visibleNavItems();
    const routes = [this.authService.getLandingRoute(), '/orders', '/customers', '/emails'];
    const [home, ...tabs] = routes.map((route) => items.find((item) => item.route === route));
    // The first tab is Home by name (MOR-D35 A), also where the menus name it "CEO dashboard"
    return [home && { ...home, icon: 'fas fa-home', translationKey: 'home' }, ...tabs].filter(
      (item): item is NavItem => !!item
    );
  });

  private notificationService = inject(NotificationCenterService);
  private filingService = inject(AttachmentFilingService);

  /** "To file" N on Emails (card 845): the emails whose attachments wait to be filed */
  readonly toFileCount = this.filingService.openCount;

  constructor(
    private authService: AuthService,
    private router: Router,
    public translationService: TranslationService
  ) {}

  ngOnInit(): void {
    // The bell stays live while the signed-in shell is open (card 927); leaving it (sign-out) closes the socket
    this.notificationService.connect();

    // Only staff can see the list; new emails come in all day, so N is read again every 5 minutes
    if (this.authService.canManageOrders()) {
      this.filingService.refreshOpenCount();
      interval(TO_FILE_COUNT_EVERY_MS)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.filingService.refreshOpenCount());
    }

    // Close mobile menu on navigation
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isMobileMenuOpen.set(false);
        this.showUserMenu.set(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.notificationService.disconnect();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Close user menu if clicking outside
    if (this.showUserMenu() && !target.closest('.user-menu-container')) {
      this.showUserMenu.set(false);
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen.update((value) => !value);
    this.showUserMenu.set(false);
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showUserMenu.update((value) => !value);
  }

  t(key: string): string {
    return this.translationService.t(key);
  }

  /** The "To file" N an item shows: Emails only, none at 0 */
  badgeFor(item: NavItem): string {
    const count = item.route === '/emails' ? (this.toFileCount() ?? 0) : 0;
    if (count <= 0) return '';
    return count > 99 ? '99+' : String(count);
  }

  /** The item's name for a screen reader, with its "To file" N */
  navLabel(item: NavItem): string {
    const label = this.t('nav.' + item.translationKey);
    const badge = this.badgeFor(item);
    return badge
      ? `${label}, ${this.translationService.t('nav.toFileBadge', { count: badge })}`
      : label;
  }

  navigateToNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
    this.showUserMenu.set(false);
  }

  get currentUser() {
    return this.authService.currentUser();
  }

  get notificationCount(): number {
    return this.notificationService.counts().unread;
  }

  get notificationBadge(): string {
    return this.notificationService.unreadBadge();
  }

  get hasUrgentNotifications(): boolean {
    return this.notificationService.hasUrgent();
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  get dashboardRoute(): string {
    return this.authService.getLandingRoute();
  }

  isRouteActive(route: string): boolean {
    // For orders, match any route starting with /orders
    if (route === '/orders') {
      return this.router.url.startsWith('/orders');
    }
    return this.router.isActive(route, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }
}
